import {
  Blocks,
  ChartColumnIncreasing,
  ChartNoAxesCombined,
  CheckCheck,
  DatabaseZap,
  Hammer,
  Link,
  Server,
  Settings,
  SlidersHorizontal,
  Spline,
  SquareActivity,
  TvMinimalPlay,
  UsersRound,
} from "lucide-react";

import { SparklesSearch } from "@/components/icons/svg/figma/SparklesSearch";

import { RolePermissions } from "@/access-control/types";
import { CanViewDestinationRanks } from "@/admin/useCanViewDestinationRanks";
import { allowAuditSelfServe } from "@/AuditLogs";
import { GetAccessReponse } from "@/entitlements/types";
import FeatureFlag from "@/FeatureFlag";
import { AccountMetadata } from "@/reducers/environments";
import { UsersState } from "@/reducers/users";
import { wtmURLs as WtmUrlsType } from "@/trackers/useWTMURLs";
import { isAccountInHIPAAMode, userIsFPEmployee } from "@/util";
import { NavItemType, RenderNavItemsPropsItem } from "./types";
import FreshpaintUser from "@/components/users/FreshpaintUser";

// System Status Navigation Item
export const getSystemStatusNavItems = (hasAdminAccess: boolean) => [
  {
    title: "System Status",
    href: "/system-status",
    icon: SquareActivity,
    shouldShowFn: () => hasAdminAccess,
  },
];

export const getFreshpaintAINavItems = (): RenderNavItemsPropsItem[] => [
  {
    title: "Freshpaint AI",
    href: "/ai-chat",
    icon: SparklesSearch,
    newSince: new Date("2026-06-17T00:00:00Z"),
    ttlDays: 30,
    featureKey: "ai_chat",
  },
];

// Setup Navigation Items
export const getSetupNavItems = () => [
  {
    title: "Sources",
    href: "/sources",
    icon: SlidersHorizontal,
  },
];

// Event Navigation Items
export const getEventNavItems = (
  accountMetadata: AccountMetadata,
  canEditEvents: boolean,
  hasGlobalAllowListEntries: boolean,
) => [
  {
    title: "Events",
    href: "/events",
    icon: Spline,
    items: [
      {
        title: "Library",
        href: "/events/library",
        shouldShowFn: () => canEditEvents,
        activeHrefPrefixes: ["/events/library/new"],
      },
      {
        title: "Live View",
        href: "/events/liveview",
      },
      {
        title: "Event Verification",
        href: "/event-verification",
      },
      {
        title: "Visual Tagger",
        href: "/events/editor",
        shouldShowFn: () => canEditEvents,
      },
      {
        title: "Transformations",
        href: "/transformations",
        activeHrefPrefixes: ["/transformations/new"],
      },
      {
        title: "HIPAA Allow List",
        href: "/hipaa/allowlist",
        shouldShowFn: () =>
          accountMetadata &&
          hasGlobalAllowListEntries &&
          isAccountInHIPAAMode(accountMetadata),
      },
      {
        title: "SQL Editor",
        href: "/sql-editor",
        shouldShowFn: () => !!accountMetadata.config.sqlTransformations,
      },
    ],
  },
];

export const getIntegrationNavItems = (
  accountMetadata: AccountMetadata,
  flag: (featureName: FeatureFlag) => boolean,
  canEditDestinations: boolean,
  canAccessAudiences: boolean,
  _accountEntitlements: GetAccessReponse | undefined,
): NavItemType[] => [
  {
    title: "Destinations",
    href: "/destinations",
    icon: Blocks,
    // Prevent parent from being active when children are active
    exact: true,
    items: [
      {
        title: "Overview",
        href: "/destinations/overview",
        activeHrefPrefixes: ["/destinations/overview"],
      },
      {
        title: "Search & Social",
        href: "/destinations/direct-response-ads",
        activeHrefPrefixes: [
          "/destinations/direct-response-ads",
          "/destinations/apps",
        ],
      },
      {
        title: "Display",
        href: "/destinations/demand-side-platforms",
        activeHrefPrefixes: [
          "/destinations/demand-side-platforms",
          "/destinations/apps",
        ],
      },
      {
        title: "Audio",
        href: "/destinations/audio",
        activeHrefPrefixes: ["/destinations/audio", "/destinations/apps"],
      },
      {
        title: "Analytics & Engagement",
        href: "/destinations/data-activation",
        activeHrefPrefixes: [
          "/destinations/data-activation",
          "/destinations/apps",
        ],
      },
      {
        title: "Audience Targeting",
        href: "/destinations/audiences",
        activeHrefPrefixes: ["/destinations/audiences", "/destinations/apps"],
        shouldShowFn: () => flag("audiences") && canAccessAudiences,
      },
      {
        title: "Data Warehouses",
        href: "/destinations/warehouses",
        activeHrefPrefixes: ["/destinations/warehouses", "/destinations/apps"],
      },
      {
        title: "Data Connectors",
        href: "/destinations/data-connectors",
        activeHrefPrefixes: ["/destinations/data-connectors"],
        shouldShowFn: () => flag("dataConnectors"),
      },
      {
        title: "CTV",
        href: "/destinations/ctv",
        activeHrefPrefixes: ["/destinations/ctv", "/destinations/apps"],
      },
      {
        title: "Mobile Measurement",
        href: "/destinations/mmp",
        activeHrefPrefixes: ["/destinations/mmp", "/destinations/apps"],
      },

      {
        title: "Functions",
        href: "/destinations/functions",
        activeHrefPrefixes: ["/destinations/functions", "/destinations/apps"],
        shouldShowFn: () =>
          !!accountMetadata.config.functions && canEditDestinations,
      },
      {
        title: "Catalog",
        href: "/destinations/catalog",
        activeHrefPrefixes: ["/destinations/catalog"],
      },
    ],
  },
];

// Analytics Navigation Items
export const getAnalyticsNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
) => [
  {
    title: "Analytics",
    href: "/analytics/dashboard",
    icon: ChartColumnIncreasing,
    shouldShowFn: () => flag("analytics"),
    items: [
      {
        title: "Dashboard",
        href: "/analytics/dashboard",
        shouldShowFn: () => flag("analytics"),
      },
      {
        title: "Campaigns",
        href: "/analytics/campaigns",
        shouldShowFn: () => flag("analytics"),
      },
      {
        title: "Service Lines",
        href: "/analytics/service-lines",
        shouldShowFn: () => flag("analytics"),
      },
    ],
  },
];

// Insights Navigation Items
export const getInsightsNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
  canAccessOfflineAttributions: boolean,
): RenderNavItemsPropsItem[] => [
  {
    title: "Insights",
    href: "/attribution",
    icon: ChartNoAxesCombined,
    featureKey: "insights",
    newSince: new Date("2026-06-01T00:00:00Z"),
    ttlDays: 30,
    items: [
      {
        title: "Attribution",
        href: "/attribution",
        exact: true,
        tracking: "data-insights-navigation",
      },
      {
        title: "Ad Performance",
        href: "/marketing-performance-analysis",
        activeHrefPrefixes: ["/marketing-performance-analysis"],
        shouldShowFn: () =>
          canAccessOfflineAttributions && flag("marketingPerformanceAnalysis"),
      },
      {
        title: "Web Analytics",
        href: "/web-analytics",
        newSince: new Date("2026-06-17T00:00:00Z"),
        ttlDays: 30,
        featureKey: "web_analytics",
      },
      {
        title: "Funnels",
        href: "/funnels",
        activeHrefPrefixes: ["/funnels"],
        newSince: new Date("2026-06-17T00:00:00Z"),
        ttlDays: 30,
        featureKey: "funnels",
      },
      {
        title: "Heatmaps",
        href: "/heatmaps",
        newSince: new Date("2026-06-17T00:00:00Z"),
        ttlDays: 30,
        featureKey: "heatmaps",
      },
      {
        title: "Session Replay",
        href: "/session-replay",
        newSince: new Date("2026-06-17T00:00:00Z"),
        ttlDays: 30,
        featureKey: "session_replay",
      },
      {
        title: "Surveys",
        href: "/surveys",
        activeHrefPrefixes: ["/surveys"],
        newSince: new Date("2026-06-17T00:00:00Z"),
        ttlDays: 30,
        featureKey: "surveys",
      },
      {
        title: "Personas",
        href: "/personas",
        activeHrefPrefixes: ["/personas"],
        newSince: new Date("2026-06-17T00:00:00Z"),
        ttlDays: 30,
        featureKey: "personas",
      },
    ],
  },
];

// Audience Navigation Items
export const getAudienceNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
  canAccessAudiences: boolean,
  canAccessUserProfiles: boolean,
) => {
  const hasAudiences = canAccessAudiences && flag("audiences");
  const hasUserProfiles = canAccessUserProfiles;
  // Segments M1 (tempSegmentsM1) removes the audiences overview page; segments
  // becomes the audiences landing page.
  const showOverview = hasAudiences && !flag("tempSegmentsM1");

  return [
    {
      title: "Audiences",
      href: showOverview ? "/audiences/overview" : "/audiences/segments",
      icon: UsersRound,
      items: [
        {
          title: "Overview",
          href: "/audiences/overview",
          shouldShowFn: () => showOverview,
        },
        {
          title: "Segments",
          href: "/audiences/segments",
          activeHrefPrefixes: ["/audiences/segments"],
        },
        {
          // Always shown — the page is a coming-soon splash, so gating the
          // nav defeats the discovery purpose.
          title: "Prospecting",
          href: "/audiences/prospects",
          activeHrefPrefixes: ["/audiences/prospects"],
        },
        {
          title: "Lookalike",
          href: "/audiences/lookalike",
          activeHrefPrefixes: ["/audiences/lookalike"],
          shouldShowFn: () => flag("lookalikeAudiences"),
        },
        {
          title: "Imported Audiences",
          href: "/audiences/imported",
          activeHrefPrefixes: ["/audiences/imported"],
          // tempNewUploader hides this nav shortcut (the Connect Data hub is
          // the preferred entry point), but the route itself stays live and
          // is still linked from the Audiences overview and lookalike flows
          // — never delete ImportedAudiencesListPage on the strength of this
          // flag alone.
          //
          // tempSegmentsM1 forces this link to stay visible even when
          // tempNewUploader is on: that flag also removes the Audiences
          // Overview page (see showOverview above), which is the only other
          // route to GenericCreateAudienceButton / CombinedAudiencesTable —
          // the sole mounts of the CSV uploader outside this page. Without
          // this override, both flags on at once leaves no discoverable path
          // to CSV upload at all.
          shouldShowFn: () =>
            hasAudiences &&
            (!flag("tempNewUploader") || flag("tempSegmentsM1")),
        },
        {
          title: "User Profiles",
          href: "/profiles",
          activeHrefPrefixes: ["/profiles"],
          shouldShowFn: () => hasUserProfiles,
        },
      ],
    },
  ];
};

export const getConnectDataNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
): NavItemType[] => [
  {
    title: "Connect data",
    href: "/connect-data",
    icon: DatabaseZap,
    activeHrefPrefixes: ["/connect-data"],
    shouldShowFn: () => flag("tempNewUploader"),
  },
];

export const getConsentManagementNavItems = (
  _flag: (featureName: FeatureFlag) => boolean,
) => {
  return [
    {
      title: "Consent",
      icon: CheckCheck,
      newSince: new Date("2026-06-01T00:00:00Z"),
      href: "/consent-management/overview",
      activeHrefPrefixes: ["/consent-management"],
      items: [
        {
          title: "Overview",
          href: "/consent-management/overview",
          activeHrefPrefixes: ["/consent-management/overview"],
        },
        {
          title: "Metrics",
          href: "/consent-management/metrics",
          activeHrefPrefixes: ["/consent-management/metrics"],
        },
      ],
    },
  ];
};

// Short Links Navigation Items
export const getShortLinksNavItems = () => {
  return [
    {
      title: "Short Links",
      icon: Link,
      newSince: new Date("2026-06-01T00:00:00Z"),
      href: "/short-links/overview",
      activeHrefPrefixes: ["/short-links"],
      items: [
        {
          title: "Overview",
          href: "/short-links/overview",
        },
        {
          title: "Links",
          href: "/short-links",
          exact: true,
        },
        {
          title: "Tags",
          href: "/short-links/tags",
        },
        {
          title: "Domains",
          href: "/short-links/domains",
          flag: "coming soon",
        },
      ],
    },
  ];
};

// Video Navigation Items
export const getVideoNavItems = (
  accountEntitlements: GetAccessReponse | undefined,
  canAccessVideo: boolean,
) => {
  const items: any[] = [];

  const isVideoEnabled = accountEntitlements?.account_access.some(
    (access: any) => access.entitlement_key === "video",
  );

  if (isVideoEnabled && canAccessVideo) {
    items.push({
      title: "Video",
      href: "/video",
      openNewTab: false,
      icon: TvMinimalPlay,
    });
  }

  return items;
};

export const getWebTrackerMonitoringNavItems = (
  wtmURLs: WtmUrlsType,
  flag: (featureName: FeatureFlag) => boolean,
  accountMetadata: AccountMetadata,
  canAccessWtm: boolean,
) =>
  canAccessWtm && accountMetadata.config.webTrackerMonitoring
    ? [
        {
          title: "Web Trackers",
          href: wtmURLs.trackersOverview,
          icon: SquareActivity,
          shouldShowFn: () => flag("webTrackerMonitoring"),
        },
      ]
    : [];

// Internal Tools Navigation Items
export const getInternalToolsNavItems = (
  canViewDestinationRanks: CanViewDestinationRanks,
  flag: (featureName: FeatureFlag) => boolean,
) => [
  {
    title: "Internal Tools",
    href: "/admin",
    icon: Hammer,
    shouldShowFn: userIsFPEmployee,
    items: [
      {
        title: "Masquerade",
        href: "/masquerade",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "MTU Usage",
        href: "/admin/mtureports",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Entitlements",
        href: "/admin/entitlements",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "GTM Migration",
        href: "/admin/gtmmigration",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Video Extractor",
        href: "/admin/videoextractor",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Cross Domain Tracking",
        href: "/admin/crossdomaintracking",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Identity Debugger",
        href: "/admin/identity-debugger",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Epic Share Link",
        href: "/admin/epicsharelink",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "AdBlock Settings",
        href: "/admin/adblock",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Ad Performance",
        href: "/admin/adperformance",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Integration Ranks",
        href: "/admin/destination-ranks",
        shouldShowFn: () =>
          userIsFPEmployee() && canViewDestinationRanks.canViewDestinationRanks,
      },
      {
        title: "Integration Apps",
        href: "/destinations/apps",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Accounts",
        href: "/admin/accounts",
        shouldShowFn: userIsFPEmployee,
        activeHrefPrefixes: ["/admin/accounts"],
      },
      {
        title: "Docs Temp Access",
        href: "/admin/docs-temp-access",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Prototype Lab",
        href: "/admin/prototypes",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Accessibility",
        href: "/accessibility",
        activeHrefPrefixes: ["/accessibility"],
        shouldShowFn: () =>
          userIsFPEmployee() && flag("accessibilityCompliance"),
      },
      {
        title: "Audience Auditor",
        href: "https://chatgpt.com/g/g-6967dc1643d48191a377156ab9d9be05-audience-auditor",
        shouldShowFn: userIsFPEmployee,
        openNewTab: true,
      },
      {
        title: "AI Visibility",
        href: "/admin/ai-visibility",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Bulk Operations",
        href: "/bulk-operations",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "AI Token Usage",
        href: "/admin/ai-token-usage",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Money Saved Report",
        href: "/money-saved",
        shouldShowFn: userIsFPEmployee,
      },
    ],
  },
];

// WTM Admin Navigation Items
export const getWTMAdminNavItems = () => [
  {
    title: "WTM Internal",
    href: "/admin",
    icon: Server,
    shouldShowFn: userIsFPEmployee,
    items: [
      {
        title: "Catalog",
        href: "/admin/trackers/catalog",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Manage Recurring Scans",
        href: "/admin/trackers/scanMonitoring",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Onboarded Domains",
        href: "/admin/trackers/domainAdmin",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Set Up Domain",
        href: "/admin/trackers/setUpDomain",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Latest Reports",
        href: "/admin/trackers/reportAdmin",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Search",
        href: "/admin/trackers/reportsByTrackerDomain",
        shouldShowFn: userIsFPEmployee,
      },
      {
        title: "Scan Sites",
        href: "/admin/trackers/siteScanner",
        shouldShowFn: userIsFPEmployee,
      },
    ],
  },
];

// Settings Navigation Items
export const getSettingsNavItems = (
  flag: (featureName: FeatureFlag) => boolean,
  usersState: UsersState,
  rolePermissions: RolePermissions | undefined,
): RenderNavItemsPropsItem[] => [
  {
    title: "Settings",
    href: "/settings",
    icon: Settings,
    items: [
      {
        title: "Team Members",
        href: "/settings/team",
      },
      {
        title: "Service Accounts",
        href: "/settings/service-accounts",
        shouldShowFn: () =>
          flag("serviceAccounts") &&
          (userIsFPEmployee() ||
            new FreshpaintUser(usersState.currentUser).hasAdminAccess()),
      },
      {
        title: "Projects",
        href: "/settings/projects",
      },
      {
        title: "Audit Logs",
        href: "/audit-logs",
        shouldShowFn: () =>
          allowAuditSelfServe(flag, usersState.currentUser, rolePermissions),
      },
    ],
  },
];
